import _ from "lodash";
import { getTableConfig, saveTableConfig } from "../../common/actions";
import { entities } from "../../common/constants";
import { computeTableConfig } from "../../../utils/tableUtils";
import {
    buildAssetViewFieldDefaults,
    mergeDynamicFieldsIntoDefaults,
    alignAssetViewFieldConfigOrder,
    getAssetViewFieldBand,
    dynamicFieldConfigKey,
    ASSET_VIEW_ALWAYS_VISIBLE_KEYS
} from "./assetViewFieldConfig";
import {
    buildAssetViewFieldCacheKey,
    getCachedAssetViewTableSettings,
    setCachedAssetViewTableSettings,
    mergeAssetViewTableSettings,
    getPersistedAssetViewTableSettings,
    hasStoredAssetViewTableSettings
} from "./assetViewFieldConfigCache";

export const isExplicitlyHidden = value =>
    value === false || value === 0 || value === "0" || value === "false";

export const normalizeVisibilityValue = value => {
    if (isExplicitlyHidden(value)) return false;
    if (value === true || value === 1 || value === "1" || value === "true") return true;
    return undefined;
};

export const applyVisibilityOverrides = (fieldConfig, overrides = {}) => {
    if (!fieldConfig?.config || !overrides || typeof overrides !== "object") return fieldConfig;
    const config = { ...fieldConfig.config };
    Object.entries(overrides).forEach(([key, entry]) => {
        if (!entry || typeof entry !== "object") return;
        const visibility = normalizeVisibilityValue(entry.isVisible);
        if (visibility === undefined) return;
        config[key] = { ...(config[key] || {}), ...entry, isVisible: visibility };
    });
    return { ...fieldConfig, config };
};

export const buildAssetViewTableSettingsPayload = (fieldConfig, dynamicBands = {}) => {
    const defaults = buildDefaultAssetViewFieldConfig(dynamicBands);
    const config = {};
    Object.entries(fieldConfig?.config || {}).forEach(([key, entry]) => {
        const defaultVisible = !isExplicitlyHidden(defaults.config[key]?.isVisible);
        const currentVisible = !isExplicitlyHidden(entry?.isVisible);
        if (currentVisible !== defaultVisible) {
            config[key] = {
                isVisible: currentVisible,
                ...(entry?.pinned !== undefined ? { pinned: entry.pinned } : {}),
                ...(entry?.style ? { style: entry.style } : {})
            };
        }
    });
    return {
        keys: fieldConfig?.keys || [],
        config
    };
};

const resolveTableSettings = (cacheKey, { useMemoryCache = true, refetchResult = null } = {}) => {
    const memorySettings = useMemoryCache ? getCachedAssetViewTableSettings(cacheKey) : undefined;
    const persistedSettings = getPersistedAssetViewTableSettings(cacheKey);
    let tableSettings = refetchResult ?? memorySettings ?? persistedSettings ?? null;
    if (refetchResult && persistedSettings) {
        tableSettings = mergeAssetViewTableSettings(refetchResult, persistedSettings);
    } else if (refetchResult == null && memorySettings && persistedSettings) {
        tableSettings = mergeAssetViewTableSettings(memorySettings, persistedSettings);
    }
    return tableSettings;
};

export const getDynamicBandsSignature = (dynamicBands = {}) => {
    if (!dynamicBands || typeof dynamicBands !== "object") return "";
    return Object.entries(dynamicBands)
        .sort(([a], [b]) => String(a).localeCompare(String(b)))
        .map(([band, fields]) => {
            const names = (Array.isArray(fields) ? fields : [])
                .map(f => f?.field_name)
                .filter(Boolean)
                .sort()
                .join(",");
            return `${band}:[${names}]`;
        })
        .join("|");
};

export const bootstrapAssetViewFieldConfig = (dynamicBands = {}, layoutContext = "view") =>
    alignAssetViewFieldConfigOrder(buildDefaultAssetViewFieldConfig(dynamicBands), dynamicBands, layoutContext);

export { getAssetViewFieldBand };

export const getRequiredAssetViewFieldKeys = (role, assetState = {}, dynamicBands = {}) => {
    const required = new Set(["asset_name", "client_id", "region_id", "site_id", "building_id"]);

    if (dynamicBands && typeof dynamicBands === "object") {
        Object.values(dynamicBands).forEach(fields => {
            if (!Array.isArray(fields)) return;
            fields.forEach(field => {
                if (field?.rules?.mandatory && field?.field_name) {
                    required.add(`df__${field.field_name}`);
                }
            });
        });
    }

    return required;
};

export const enforceRequiredFieldVisibility = (fieldConfig, requiredKeys) => {
    const config = { ...fieldConfig.config };
    const forceVisible = new Set([...requiredKeys, ...ASSET_VIEW_ALWAYS_VISIBLE_KEYS]);
    forceVisible.forEach(key => {
        if (config[key]) {
            config[key] = { ...config[key], isVisible: true };
        }
    });
    return { ...fieldConfig, config };
};

export const buildDefaultAssetViewFieldConfig = (dynamicBands = {}) =>
    mergeDynamicFieldsIntoDefaults(buildAssetViewFieldDefaults(), dynamicBands);

const applyAssetViewFieldConfigFromSettings = (tableSettings, { dynamicBands, role, assetState, layoutContext }) => {
    const defaults = buildDefaultAssetViewFieldConfig(dynamicBands);
    const requiredKeys = getRequiredAssetViewFieldKeys(role, assetState, dynamicBands);
    const { config: computedConfig, keys: computedKeys } = computeTableConfig({
        defaultConfig: defaults.config,
        defaultKeys: defaults.keys,
        tableSettings
    });
    const aligned = alignAssetViewFieldConfigOrder({ keys: computedKeys, config: computedConfig }, dynamicBands, layoutContext);
    const withVisibility = applyVisibilityOverrides(aligned, tableSettings?.config);
    return enforceRequiredFieldVisibility(withVisibility, requiredKeys);
};

export const resolveAssetViewFieldConfig = async ({
    clientId,
    dynamicBands = {},
    role,
    assetState,
    layoutContext = "view",
    refetch = false
}) => {
    const defaults = buildDefaultAssetViewFieldConfig(dynamicBands);
    const requiredKeys = getRequiredAssetViewFieldKeys(role, assetState, dynamicBands);

    if (!clientId) {
        const fieldConfig = enforceRequiredFieldVisibility(
            alignAssetViewFieldConfigOrder(defaults, dynamicBands, layoutContext),
            requiredKeys
        );
        return { fieldConfig, fromCache: false, cacheKey: null };
    }

    const cacheKey = buildAssetViewFieldCacheKey({ clientId });
    const useCache = !refetch && hasStoredAssetViewTableSettings(cacheKey);
    let tableSettings;

    if (useCache) {
        tableSettings = resolveTableSettings(cacheKey, { useMemoryCache: true });
    } else {
        const previousSettings = resolveTableSettings(cacheKey, { useMemoryCache: true });
        const response = await getTableConfig({
            client_id: clientId,
            table_type: entities.ASSET_VIEW
        });
        // merging the cached copy into the response would keep a field hidden after an unhide
        tableSettings = response ? (response?.data?.table_settings ?? null) : (previousSettings ?? null);
        setCachedAssetViewTableSettings(cacheKey, tableSettings);
    }

    const fieldConfig = applyAssetViewFieldConfigFromSettings(tableSettings, {
        dynamicBands,
        role,
        assetState,
        layoutContext
    });
    return { fieldConfig, fromCache: useCache, cacheKey };
};

export const revalidateAssetViewFieldConfig = async ({
    clientId,
    dynamicBands = {},
    role,
    assetState,
    layoutContext = "view"
}) => {
    if (!clientId) return null;
    const cacheKey = buildAssetViewFieldCacheKey({ clientId });
    const previousSettings = resolveTableSettings(cacheKey, { useMemoryCache: true });
    const response = await getTableConfig({
        client_id: clientId,
        table_type: entities.ASSET_VIEW
    });
    if (!response) return null;
    const apiSettings = response?.data?.table_settings ?? null;
    if (_.isEqual(apiSettings, previousSettings)) return null;
    setCachedAssetViewTableSettings(cacheKey, apiSettings);
    return applyAssetViewFieldConfigFromSettings(apiSettings, { dynamicBands, role, assetState, layoutContext });
};

export const recomputeAssetViewFieldConfig = ({ clientId, dynamicBands = {}, role, assetState, layoutContext = "view" }) => {
    const cacheKey = buildAssetViewFieldCacheKey({ clientId });
    const tableSettings = resolveTableSettings(cacheKey);
    return applyAssetViewFieldConfigFromSettings(tableSettings, {
        dynamicBands,
        role,
        assetState,
        layoutContext
    });
};

export const saveAssetViewFieldConfig = async ({ clientId, keys, config, dynamicBands = {} }) => {
    if (!clientId) return;
    const table_settings = buildAssetViewTableSettingsPayload({ keys, config }, dynamicBands);
    await saveTableConfig({
        client_id: clientId,
        table_type: entities.ASSET_VIEW,
        table_settings
    });
    setCachedAssetViewTableSettings(buildAssetViewFieldCacheKey({ clientId }), table_settings);
};

export const resetAssetViewFieldConfig = async ({ clientId }) => {
    if (!clientId) return null;
    await saveTableConfig({
        client_id: clientId,
        table_type: entities.ASSET_VIEW,
        table_settings: null
    });
    setCachedAssetViewTableSettings(buildAssetViewFieldCacheKey({ clientId }), null);
    return null;
};

export const isAssetViewFieldVisible = (key, fieldConfig) => {
    if (ASSET_VIEW_ALWAYS_VISIBLE_KEYS.has(key)) return true;
    return !isExplicitlyHidden(fieldConfig?.config?.[key]?.isVisible);
};

export const getHiddenAssetViewFieldCount = fieldConfig => {
    if (!fieldConfig?.config) return 0;
    return Object.keys(fieldConfig.config).filter(
        key => !ASSET_VIEW_ALWAYS_VISIBLE_KEYS.has(key) && isExplicitlyHidden(fieldConfig.config[key]?.isVisible)
    ).length;
};

export const hasActiveAssetViewFieldCustomization = fieldConfig => getHiddenAssetViewFieldCount(fieldConfig) > 0;

export const isAssetViewFieldConfigChanged = (current, initial) => {
    if (!current?.config || !initial?.config) return false;
    const keys = new Set([...Object.keys(current.config), ...Object.keys(initial.config)]);
    for (const key of keys) {
        if (
            !_.isEqual(
                !isExplicitlyHidden(current.config[key]?.isVisible),
                !isExplicitlyHidden(initial.config[key]?.isVisible)
            )
        ) {
            return true;
        }
    }
    return false;
};

export const isDynamicBandVisible = (fields, fieldConfig) => {
    if (!Array.isArray(fields) || !fields.length) return false;
    return fields.some(f => f?.field_name && isAssetViewFieldVisible(`df__${f.field_name}`, fieldConfig));
};

const bandVisibilityExcludedKeys = ASSET_VIEW_ALWAYS_VISIBLE_KEYS;

export const isAssetViewBandVisible = (bandName, fieldConfig, layoutContext = "view") => {
    if (!bandName || !fieldConfig?.config) return false;
    return (fieldConfig.keys || []).some(key => {
        const entry = fieldConfig.config[key];
        if (!entry || bandVisibilityExcludedKeys.has(key)) return false;
        if (entry.isDynamicField || String(key).startsWith("df__")) return false;
        return getAssetViewFieldBand(key, fieldConfig, layoutContext) === bandName && isAssetViewFieldVisible(key, fieldConfig);
    });
};

export const isAssetViewFieldConfigReady = (fieldConfig, configReady) => !!configReady && !!fieldConfig?.config;

const TABLE_COLUMN_TO_VIEW_KEY = {
    asset_type: "asset_type_id",
    asset_status: "asset_status_id",
    client_asset_condition: "client_asset_condition_id",
    description: "asset_condition_description",
    main_category: "main_category_id",
    sub_category_1: "sub_category_1_id",
    sub_category_2: "sub_category_2_id",
    sub_category_3: "sub_category_3_id",
    trade: "trade_id",
    system: "system_id",
    sub_system: "sub_system_id",
    uniformat_level_1: "uniformat_level_1_id",
    uniformat_level_2: "uniformat_level_2_id",
    uniformat_level_3: "uniformat_level_3_id",
    uniformat_level_4: "uniformat_level_4_id",
    uniformat_level_5: "uniformat_level_5_id",
    uniformat_level_6: "uniformat_level_6_id",
    client_standards: "client_standards_id",
    client: "client_id",
    region: "region_id",
    site: "site_id",
    building: "building_id",
    addition: "addition_id",
    floor: "floor_id"
};

export const resolveAssetTableColumnConfigKey = (tableKey, tableColEntry) => {
    if (tableColEntry?.isDynamicField) return dynamicFieldConfigKey(tableKey);
    return TABLE_COLUMN_TO_VIEW_KEY[tableKey] || tableKey;
};

export const resolveAssetFilterFieldConfigKey = field =>
    field?.key ? resolveAssetTableColumnConfigKey(field.key, field) : null;

// multiEditKey is already a field-config key (asset_type_id, …)
export const resolveAssetMultiEditFieldConfigKey = field => field?.multiEditKey || resolveAssetFilterFieldConfigKey(field);

const isViewFieldHidden = (viewKey, assetViewFieldConfig) => {
    const viewEntry = viewKey ? assetViewFieldConfig?.config?.[viewKey] : null;
    return !!viewEntry && isExplicitlyHidden(viewEntry.isVisible);
};

export const isAssetTableColumnAdminHidden = (tableKey, tableColEntry, assetViewFieldConfig) =>
    isViewFieldHidden(resolveAssetTableColumnConfigKey(tableKey, tableColEntry), assetViewFieldConfig);

export const isAssetFilterFieldAdminHidden = (field, assetViewFieldConfig) =>
    isViewFieldHidden(resolveAssetFilterFieldConfigKey(field), assetViewFieldConfig);

export const isAssetMultiEditFieldAdminHidden = (field, assetViewFieldConfig) =>
    isViewFieldHidden(resolveAssetMultiEditFieldConfigKey(field), assetViewFieldConfig);

export const applyAssetViewVisibilityToTableConfig = (tableConfig, assetViewFieldConfig) => {
    const keys = tableConfig?.keys || [];
    const config = tableConfig?.config || {};
    if (!assetViewFieldConfig?.config) return { keys, config };
    const nextConfig = { ...config };
    let changed = false;
    keys.forEach(key => {
        const entry = nextConfig[key];
        if (!entry || !isAssetTableColumnAdminHidden(key, entry, assetViewFieldConfig)) return;
        nextConfig[key] = { ...entry, isVisible: false, isAdminHidden: true };
        changed = true;
    });
    return changed ? { keys, config: nextConfig } : { keys, config };
};

export const getAdminHiddenColumnKeys = ({ keys = [], config = {} } = {}) => keys.filter(key => config[key]?.isAdminHidden);

// only for the column-chooser modal — the caller keeps the full keys, which export reads for hide_columns
export const splitAdminHiddenColumns = ({ keys = [], config = {} } = {}) => {
    const visibleKeys = keys.filter(key => !config[key]?.isAdminHidden);
    if (visibleKeys.length === keys.length) return { keys, config };
    const nextConfig = {};
    visibleKeys.forEach(key => {
        if (config[key]) nextConfig[key] = config[key];
    });
    return { keys: visibleKeys, config: nextConfig };
};

export const mergeAdminHiddenColumns = (current, next) => {
    const currentKeys = current?.keys || [];
    const currentConfig = current?.config || {};
    const hiddenKeys = getAdminHiddenColumnKeys({ keys: currentKeys, config: currentConfig });
    if (!hiddenKeys.length) return next;
    const keys = [...(next?.keys || [])];
    const config = { ...(next?.config || {}) };
    hiddenKeys.forEach(key => {
        config[key] = currentConfig[key];
        if (keys.includes(key)) return;
        const precedingKey = currentKeys
            .slice(0, currentKeys.indexOf(key))
            .reverse()
            .find(candidate => keys.includes(candidate));
        keys.splice(precedingKey ? keys.indexOf(precedingKey) + 1 : 0, 0, key);
    });
    return { ...next, keys, config };
};

// an admin restriction is not a user preference: saving it would outlive the admin showing the field again
export const stripAdminHiddenColumnsFromSave = ({ keys = [], config = {} } = {}) => {
    const nextConfig = {};
    Object.entries(config).forEach(([key, entry]) => {
        if (!entry?.isAdminHidden) nextConfig[key] = entry;
    });
    return { keys, config: nextConfig };
};
